import { Head, Link } from "@inertiajs/react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Gauge,
  LayoutTemplate,
  Link2,
  Lock,
  Settings2,
  Sparkles,
  WandSparkles,
  Workflow,
} from "lucide-react";
import type { ComponentType } from "react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface SetupStep {
  title: string;
  description: string;
}

interface FeatureCard {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}

interface WordPressTemplateExample {
  name: string;
  best_for: string;
  fields: string;
  src: string;
  alt: string;
  brand?: string | null;
  template?: string | null;
}

interface WordPressFaq {
  question: string;
  answer: string;
}

interface WordPressSocialImageProps {
  page_title?: string;
  faqs?: WordPressFaq[];
  wordpress_template_examples?: WordPressTemplateExample[];
}

interface ComparisonRow {
  dimension: string;
  plugin: string;
  api: string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    dimension: "Best fit",
    plugin: "Posts, pages, and WooCommerce products that should get OG tags without a theme hook.",
    api: "A PHP, Ruby, JavaScript, or Python app, or an agent that should create the card.",
  },
  {
    dimension: "What you add",
    plugin: "The OG Pilot WordPress plugin. Connect with OAuth. No API key in the theme.",
    api: "An API key and a server-signed request, or the remote MCP connector.",
  },
  {
    dimension: "Auth this app implements",
    plugin:
      "OAuth with PKCE (S256). A signed-in account links the site. The plugin then calls the image API with a bearer token.",
    api: "HS256 JWT. Required claims are iss (verified domain) and sub (API key prefix).",
  },
  {
    dimension: "og:image",
    plugin:
      "The plugin prints that CDN URL as og:image. If generation has no URL yet, it prints the featured image instead, and it skips processing placeholders.",
    api: "Your template prints the returned URL in og:image and twitter:image.",
  },
  {
    dimension: "og:title, og:description, og:url",
    plugin:
      "With Open Graph output on, the plugin prints og:title, og:description, and og:url. This app only returns the image URL.",
    api: "You write them. The image API does not.",
  },
  {
    dimension: "Theme tags",
    plugin: "Turn the theme hook off, or the page can print two sets of tags.",
    api: "Same rule: one writer for each property.",
  },
  {
    dimension: "Yoast, Rank Math, AIOSEO",
    plugin:
      "Meta Ownership set to External SEO plugin makes the plugin skip its tags. AIOSEO's default post image source is the option you pick on its Facebook settings page.",
    api: "You decide whether your template also prints tags. Do not double-print og:image.",
  },
  {
    dimension: "Domain check",
    plugin: "WordPress OAuth marks the site domain verified. There is no DNS TXT step in that flow.",
    api: "The API docs require a DNS TXT record before the API generates images.",
  },
  {
    dimension: "When the title changes",
    plugin:
      "The listing's placeholders, including {{post_title}}, are filled into the title and description. Auto-generate on save queues a new image.",
    api: "Send the new title on the next signed request. Pass iat for the documented 24-hour cache window.",
  },
  {
    dimension: "Latency and cost",
    plugin: "Plugin FAQ: most images ready in 5–10 minutes via WP-Cron. That line is not measured here.",
    api: "Plan catalog: Starter $5/month, Builder $49/month, Growth $99/month. No per-image price in that catalog.",
  },
];

const THEME_META_SAMPLE = `<?php
// Example you maintain. WordPress core does not print og:image.
add_action( 'wp_head', function () {
    if ( ! is_singular() || ! has_post_thumbnail() ) {
        return;
    }

    $image = wp_get_attachment_image_src( get_post_thumbnail_id(), 'full' );
    if ( ! $image ) {
        return;
    }

    $url = esc_url( $image[0] );
    echo '<meta property="og:image" content="' . $url . '" />' . "\\n";
    echo '<meta property="og:image:width" content="' . (int) $image[1] . '" />' . "\\n";
    echo '<meta property="og:image:height" content="' . (int) $image[2] . '" />' . "\\n";
    echo '<meta name="twitter:card" content="summary_large_image" />' . "\\n";
    echo '<meta name="twitter:image" content="' . $url . '" />' . "\\n";
} );`;

const PLUGIN_SETUP_SAMPLE = `1. Install OG Pilot Dynamic Open Graph Images
   https://wordpress.org/plugins/og-pilot-dynamic-open-graph-images/
2. Activate it. Admins are redirected to the setup wizard.
3. Settings → OG Pilot Open Graph Images
4. Connect with OG Pilot (OAuth + PKCE). No API key in the theme.
5. Choose a default template and the content types (post, page, product).
6. Optional: Meta Ownership = External SEO plugin
   when Yoast, Rank Math, or AIOSEO should keep the tags.`;

const PLUGIN_TAG_SAMPLE = `The plugin listing says it outputs Open Graph and X/Twitter tags,
including og:image and twitter:image. Release 0.1.0 also added
og:image:url, twitter:image:src, and twitter:domain.

<meta property="og:image" content="https://cdn.ogpilot.com/..." />
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:image" content="https://cdn.ogpilot.com/..." />

Placeholders the listing documents include {{post_title}}, {{excerpt}},
and {{featured_image_url}}. The featured image can be a field on the
generated card. It is not, by itself, the og:image.`;

const PHP_API_SAMPLE = `<?php
// Server you control. Prefer the official plugin inside WordPress.
// Docs: https://ogpilot.com/docs
use Sunergos\\OgPilot\\OgPilot;

OgPilot::setConfig([
    "api_key" => getenv("OG_PILOT_API_KEY"),
    "domain" => getenv("OG_PILOT_DOMAIN"),
]);

$imageUrl = OgPilot::createImage([
    "template" => "blog_post",
    "title" => "Post title",
], ["iat" => time()]);

// Then print the URL. Do not commit the API key.
echo '<meta property="og:image" content="' . htmlspecialchars($imageUrl, ENT_QUOTES) . '" />';`;

const RAW_HTTP_SAMPLE = `POST https://ogpilot.com/api/v1/images
Content-Type: application/json

{"token":"YOUR_JWT_TOKEN"}

Required JWT claims: iss (verified domain), sub (API key prefix).
If iat is present, the image cache is valid for 24 hours from that
timestamp. Omit iat and the cache does not expire on its own.
Prefer an official SDK over hand-rolled JWT signing.`;

const MCP_SAMPLE = `{
  "mcpServers": {
    "og-pilot": {
      "type": "streamable-http",
      "url": "https://ogpilot.com/mcp"
    }
  }
}

# Auth: OAuth 2.1 with PKCE. No API key in this file.
# Claude and ChatGPT do not read this file. Follow /docs/mcp.
# Do not add command, args, or a bearer token.`;

const SETUP_STEPS: SetupStep[] = [
  {
    title: "Install and activate the plugin",
    description:
      "Install OG Pilot Dynamic Open Graph Images from WordPress.org, then activate it. Web activations send admins to the setup wizard. WP-CLI activations skip that redirect.",
  },
  {
    title: "Run the setup wizard",
    description:
      "Open Settings → OG Pilot Open Graph Images. The listing describes a 3-step wizard for the connection, the default template, and enabled content types.",
  },
  {
    title: "Connect with OG Pilot over OAuth",
    description:
      "Use Connect with OG Pilot. The plugin handles PKCE and token refresh. The FAQ says you do not paste a long-lived API secret into the theme, and the WordPress OAuth flow has no manual DNS step.",
  },
  {
    title: "Choose templates and rules",
    description:
      "Set a default template, then optional rules for post, page, product, and taxonomy slugs. Public templates listed for the plugin are page, blog_post, podcast, portfolio, book, event, product, company, and github.",
  },
  {
    title: "Publish and auto-generate",
    description:
      "With auto-generation enabled, saving a post queues a card in the background. The FAQ says page loads do not wait on generation, and a processing image is returned until the final file is ready.",
  },
];

const FEATURE_CARDS: FeatureCard[] = [
  {
    title: "OAuth + PKCE auth flow",
    description:
      "Account linking without pasting a long-lived API secret into theme files. The API path is the one that uses a server-side key.",
    icon: Lock,
  },
  {
    title: "Template system built for WordPress",
    description:
      "The plugin listing names page, blog_post, podcast, portfolio, book, event, product, company, and github.",
    icon: LayoutTemplate,
  },
  {
    title: "Rule-based automation",
    description:
      "Assign template defaults by post type and by category, tag, or product type so the card follows the content, not a one-off upload.",
    icon: Workflow,
  },
  {
    title: "Per-post overrides",
    description:
      "The OG Pilot Social Image meta box is where editors override a single post, page, or product. Admins still own the global settings.",
    icon: Settings2,
  },
  {
    title: "Background generation queue",
    description:
      "The listing says generation is non-blocking, with a processing fallback and retry scheduling, so the editor save is not the image render.",
    icon: Gauge,
  },
  {
    title: "SEO-plugin compatibility",
    description:
      "Meta Ownership set to External SEO plugin stops OG Pilot tags so Yoast, Rank Math, or AIOSEO can keep theirs. Import copies their social fields in.",
    icon: Link2,
  },
];

const INTEGRATION_HIGHLIGHTS = [
  "Official WordPress.org plugin for generated og:image and twitter:image tags.",
  "No manual DNS step in the WordPress OAuth flow. The API path still verifies the domain.",
  "Bulk regenerate queue with progress in wp-admin.",
  "Import of existing social fields from Yoast, Rank Math, and AIOSEO.",
  "When the subscription is inactive, new generation pauses and existing image URLs keep working.",
];

const pluginInstallUrl = "https://wordpress.org/plugins/og-pilot-dynamic-open-graph-images/";

const sectionClass =
  "mt-20 rounded-3xl border border-orange-200/80 bg-white/90 p-8 shadow-xl dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

const formatTemplateLabel = (template?: string | null) => (template ? template.replace(/_/g, " ") : null);

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function WordPressSocialImage({
  page_title: pageTitle = "WordPress Open Graph Plugin for OG Tags",
  faqs = [],
  wordpress_template_examples = [],
}: WordPressSocialImageProps) {
  const templateExamples = wordpress_template_examples.filter(
    (example) => Boolean(example?.src) && Boolean(example?.name),
  );

  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-red-300/35 blur-3xl dark:bg-red-500/20" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-52 w-52 rounded-full bg-orange-300/40 blur-3xl dark:bg-orange-500/20" />

            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                WordPress open graph plugin
              </Badge>

              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl lg:text-6xl dark:text-slate-100">
                WordPress Open Graph plugin for OG tags and OG images
              </h1>

              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                A <strong>WordPress Open Graph plugin</strong> decides who writes the OG tags and where the OG image
                comes from. Use a theme hook when one uploaded file is the whole preview. Use Yoast or Rank Math when an
                uploaded social image should stay the card. Install OG Pilot when posts, pages, or WooCommerce products
                should get a generated card. Use an API key or MCP when the image is signed outside WordPress. The
                step-by-step is{" "}
                <Link href="/change-og-image-wordpress" className={linkClass}>
                  how to change the OG image in WordPress
                </Link>
                . What each tag means is the{" "}
                <Link href="/open-graph-protocol-wordpress" className={linkClass}>
                  Open Graph protocol on WordPress
                </Link>
                .
              </p>

              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                AIOSEO&apos;s default post image source is a setting you choose, documented on its{" "}
                <a
                  href="https://aioseo.com/docs/setting-a-default-image-for-facebook/"
                  className={linkClass}
                  target="_blank"
                  rel="noreferrer"
                >
                  Facebook default-image page
                </a>
                . This app returns an image URL. The plugin prints <code>og:title</code>, <code>og:description</code>,{" "}
                <code>og:image</code>, and <code>og:url</code> when Open Graph output is on.{" "}
                <Link href="/open-graph-debugger" className={linkClass}>
                  Check a live preview in the Open Graph debugger.
                </Link>
              </p>

              <div className="mt-8 flex flex-col gap-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button asChild size="lg" className="bg-orange-500 text-white hover:bg-orange-600">
                    <a href={pluginInstallUrl} target="_blank" rel="noreferrer">
                      Install WordPress plugin
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/sign_up">Get API key</Link>
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs/mcp">Use MCP</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/new">Generate</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/open-graph-debugger">Debugger</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs">Docs</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              When to use each WordPress OG path
            </h2>
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
              <article
                id="featured-image"
                className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Theme meta + featured image
                </h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>One uploaded file is the card you want on every share.</li>
                  <li>
                    You are willing to keep a <code>wp_head</code> hook in the theme or a tiny plugin.
                  </li>
                  <li>
                    WordPress will not add <code>og:image</code> for you. The theme handbook only covers printing the
                    thumbnail in the template.
                  </li>
                </ul>
              </article>
              <article
                id="seo-plugin-fields"
                className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Yoast or Rank Math fields</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>The SEO plugin should own the social tags.</li>
                  <li>The artwork is an upload: a social image, the featured image, or a sitewide default.</li>
                  <li>You do not need a new layout when the title changes. The file stays the file.</li>
                </ul>
              </article>
              <article
                id="dynamic-api"
                className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60"
              >
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Dynamic OG Pilot image</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>Install the plugin when the content is posts, pages, or WooCommerce products.</li>
                  <li>Get an API key when PHP, Rails, or another app should sign the same templates.</li>
                  <li>
                    Use{" "}
                    <Link href="/docs/mcp" className={linkClass}>
                      MCP
                    </Link>{" "}
                    when an agent should create the card.
                  </li>
                </ul>
              </article>
            </div>
          </section>

          <section id="comparison" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Plugin meta tags vs dynamic API OG images
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Featured-image behavior is from the{" "}
              <a
                href="https://developer.wordpress.org/themes/functionality/featured-images-post-thumbnails/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                WordPress theme handbook
              </a>
              . Yoast&apos;s order is from its{" "}
              <a
                href="https://developer.yoast.com/features/opengraph/functional-specification/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Open Graph functional spec
              </a>
              . Rank Math&apos;s fallback and 1200×630 note are from{" "}
              <a href="https://rankmath.com/kb/titles-and-meta/" className={linkClass} target="_blank" rel="noreferrer">
                Titles and Meta
              </a>
              . OG Pilot plugin behavior is from the{" "}
              <a href={pluginInstallUrl} className={linkClass} target="_blank" rel="noreferrer">
                WordPress.org listing
              </a>
              . API cache rules are from the{" "}
              <Link href="/docs" className={linkClass}>
                API docs
              </Link>
              .
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[52rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Dimension</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      WordPress plugin meta tags
                    </th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      Dynamic API OG images
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.dimension} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.dimension}</th>
                      <td className="px-3 py-3">{row.plugin}</td>
                      <td className="px-3 py-3">{row.api}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Theme meta tags and the featured image
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              A featured image is an attachment on the post. The theme handbook says you enable it with{" "}
              <code>add_theme_support(&apos;post-thumbnails&apos;)</code> and print it with{" "}
              <code>the_post_thumbnail()</code> or <code>wp_get_attachment_image_src()</code>. That prints an{" "}
              <code>&lt;img&gt;</code> in the page. Social apps do not treat that image as <code>og:image</code> unless
              something in <code>wp_head</code> says so.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Use this path when a designer already exported the card and the URL can stay stable. Skip it when every
              post needs a different layout, or when you do not want the tag logic living in the theme.
            </p>
            <CodeSample label="Copy-paste: featured image as og:image" code={THEME_META_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Yoast and Rank Math social fields</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Yoast&apos;s{" "}
              <a
                href="https://developer.yoast.com/features/opengraph/functional-specification/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Open Graph functional spec
              </a>{" "}
              populates <code>og:image</code> only when it finds a valid image, in this order: a user-defined Facebook
              image in the Social tab, the featured image (posts only), a prominent image from the content, the social
              default from Search Appearance, then the site fallback. The current help article labels the editor{" "}
              <a
                href="https://yoast.com/help/custom-open-graph-tags/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Social media appearance
              </a>
              , where you set an image, title, and description. The spec still calls the stored field a Facebook image.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Rank Math&apos;s{" "}
              <a
                href="https://rankmath.com/kb/open-graph-meta-tags/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Open Graph guide
              </a>{" "}
              calls <code>og:title</code>, <code>og:type</code>, <code>og:url</code>, and <code>og:image</code>{" "}
              essential. The same guide lists the image order as the Social-tab OpenGraph image, then the featured
              image, then the first image in the post, then the default OpenGraph thumbnail.{" "}
              <a href="https://rankmath.com/kb/titles-and-meta/" className={linkClass} target="_blank" rel="noreferrer">
                Titles and Meta
              </a>{" "}
              says that thumbnail (Rank Math SEO → Titles &amp; Meta → Global Meta) is the fallback when a post has
              neither a featured image nor an OpenGraph image, and it recommends 1200×630.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Both plugins attach tags to a file you already have. They are the right tool when the preview is a
              photograph or a designed PNG. They are the wrong tool when the headline has to be drawn into the image on
              every publish. AIOSEO&apos;s{" "}
              <a
                href="https://aioseo.com/docs/setting-a-default-image-for-facebook/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                default Facebook image doc
              </a>{" "}
              lets you set Default Post Image Source to Default Image, Featured Image, Attached Image, First Image in
              Content, Image from Custom Field, Post Author Image, or First Available Image. The Default Post Facebook
              Image is the fallback when that source has no file. The OG Pilot plugin FAQ says Meta Ownership set to
              External SEO plugin stops OG Pilot tags so Yoast, Rank Math, or AIOSEO can keep theirs. The plugin source
              returns before printing tags in that mode.
            </p>
          </section>

          <section id="copy-paste" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Plugin setup and the dynamic API</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              On a WordPress site, install the plugin. It connects with OAuth, queues generation, and writes the tags.
              The PHP sample is for a server outside that plugin: a Laravel app, a custom integration, or a
              non-WordPress PHP site that should share the templates. Keep <code>OG_PILOT_API_KEY</code> in the
              environment. The{" "}
              <Link href="/docs" className={linkClass}>
                API docs
              </Link>{" "}
              say signing happens in your application, and that a domain must be verified with DNS TXT before the API
              generates images. How that hosted API compares with <code>@vercel/og</code> and DIY Satori is on the{" "}
              <Link href="/og-image-api" className={linkClass}>
                OG image API
              </Link>{" "}
              page. The plugin FAQ says the WordPress OAuth flow does not add that manual DNS step.
            </p>
            <CodeSample label="Plugin setup" code={PLUGIN_SETUP_SAMPLE} />
            <CodeSample label="Meta tag shape the plugin documents" code={PLUGIN_TAG_SAMPLE} />
            <CodeSample label="PHP SDK for a dynamic image (server only)" code={PHP_API_SAMPLE} />
            <CodeSample label="Raw HTTP shape" code={RAW_HTTP_SAMPLE} />
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Pass <code>iat</code> when you want the documented 24-hour cache window. Social networks can still keep an
              older scrape of the same URL. After the artwork changes,{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                re-check the permalink
              </Link>
              .
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              MCP, when an agent should make the card
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              The remote endpoint is <code>https://ogpilot.com/mcp</code>. The block below is the remote config for a
              client that accepts an <code>mcpServers</code> map. Claude and ChatGPT do not read that file. Connector
              screens differ, so follow{" "}
              <Link href="/docs/mcp" className={linkClass}>
                /docs/mcp
              </Link>{" "}
              instead of guessing a second config. MCP fits a workflow where an agent creates the image from a URL. It
              does not replace the plugin for a normal WordPress publish.
            </p>
            <CodeSample label="MCP remote config" code={MCP_SAMPLE} />
          </section>

          <section className="mt-20 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="rounded-3xl border border-orange-200/80 bg-white/90 p-8 shadow-xl dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-none">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-100 px-4 py-2 text-xs font-semibold tracking-[0.16em] text-emerald-700 uppercase dark:border-emerald-400/30 dark:bg-emerald-400/20 dark:text-emerald-200">
                <CheckCircle2 className="h-4 w-4" />
                Plugin setup flow
              </div>

              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">From install to production</h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                This is the WordPress path. The plugin handles auth, the generation queue, and social tag output for
                posts, pages, and WooCommerce products. Use the theme snippet or the raw API only when this install is
                the wrong shape.
              </p>

              <div className="mt-8 space-y-4">
                {SETUP_STEPS.map((step, index) => (
                  <article
                    key={step.title}
                    className="group rounded-2xl border border-orange-200/80 bg-orange-50/70 p-5 transition-transform duration-200 hover:-translate-y-0.5 dark:border-orange-500/25 dark:bg-slate-950/60"
                  >
                    <div className="flex items-start gap-4">
                      <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white dark:bg-slate-100 dark:text-slate-900">
                        {index + 1}
                      </span>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{step.title}</h3>
                        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">{step.description}</p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-3xl border border-slate-900/10 bg-slate-900 p-8 text-slate-100 shadow-xl dark:border-slate-700">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-800 px-3 py-1 text-xs tracking-[0.15em] uppercase">
                  <Sparkles className="h-4 w-4" />
                  Why the plugin
                </div>
                <h3 className="text-2xl font-semibold">The WordPress install of the same dynamic image</h3>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  Keep the theme free of API keys. The plugin listing is the source for the behavior in this list.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-slate-200">
                  {INTEGRATION_HIGHLIGHTS.map((item) => (
                    <li key={item} className="flex gap-2.5">
                      <Check className="mt-0.5 h-4 w-4 flex-none text-emerald-300" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-100 via-red-100 to-white p-8 shadow-lg dark:border-orange-500/25 dark:from-orange-900/30 dark:via-red-900/20 dark:to-slate-900">
                <p className="text-xs tracking-[0.18em] text-slate-600 uppercase dark:text-slate-300">
                  WordPress library
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
                  Install the plugin when the content lives in WordPress
                </h3>
                <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  Posts, pages, and products stay on the plugin. Everything else uses an API key or MCP.
                </p>
                <div className="mt-6">
                  <Button asChild size="lg" className="bg-orange-500 text-white hover:bg-orange-600">
                    <a href={pluginInstallUrl} target="_blank" rel="noreferrer">
                      Install WordPress plugin
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">
                    Listed on WordPress.org. Requires WordPress 6.5+ and PHP 8.0+, per the plugin page.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-20">
            <div className="mb-3 flex items-center gap-3">
              <WandSparkles className="h-6 w-6 text-orange-500" />
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                Templates the plugin and the API share
              </h2>
            </div>
            <p className="mb-6 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Gallery renders of templates the plugin listing names, so you can see the card before you install. The
              same template names are what the API docs accept.
            </p>

            {templateExamples.length > 0 ? (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {templateExamples.map((template, index) => (
                  <article
                    key={`${template.name}-${template.brand ?? index}`}
                    className="group overflow-hidden rounded-2xl border border-orange-200/80 bg-white/90 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl dark:border-orange-500/25 dark:bg-slate-900/70"
                  >
                    <div className="relative aspect-[1.91/1] overflow-hidden border-b border-orange-100 dark:border-orange-500/20">
                      <img
                        src={template.src}
                        alt={template.alt}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                        loading={index < 3 ? "eager" : "lazy"}
                        fetchPriority={index === 0 ? "high" : "low"}
                        decoding="async"
                      />
                      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-slate-950/85 to-transparent p-3">
                        {template.brand ? (
                          <span className="rounded-full bg-white/20 px-2 py-1 text-[11px] font-medium tracking-wide text-white backdrop-blur-sm">
                            {template.brand}
                          </span>
                        ) : (
                          <span />
                        )}
                        {template.template ? (
                          <span className="rounded-full border border-white/30 px-2 py-1 text-[11px] font-medium tracking-wide text-white uppercase">
                            {formatTemplateLabel(template.template)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{template.name}</h3>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{template.best_for}</p>
                      <p className="mt-4 rounded-xl bg-slate-900/5 px-3 py-2 text-xs leading-5 text-slate-700 dark:bg-slate-50/10 dark:text-slate-200">
                        <span className="font-semibold">Common fields:</span> {template.fields}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-orange-200/80 bg-white/90 p-6 text-sm text-slate-600 dark:border-orange-500/30 dark:bg-slate-900/70 dark:text-slate-300">
                Template examples are being prepared. Refresh this page in a few seconds.
              </div>
            )}
          </section>

          <section className={sectionClass}>
            <div className="mb-7 flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-orange-500" />
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                What the WordPress plugin takes off the theme
              </h2>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {FEATURE_CARDS.map((feature) => (
                <article
                  key={feature.title}
                  className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 transition-colors hover:bg-orange-100/70 dark:border-orange-500/20 dark:bg-slate-950/60 dark:hover:bg-slate-950"
                >
                  <feature.icon className="h-5 w-5 text-orange-500" />
                  <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-slate-100">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{feature.description}</p>
                </article>
              ))}
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Queue time and plan prices</h2>
            <ul className="mt-4 list-disc space-y-3 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                <strong>Plugin queue.</strong> The{" "}
                <a href={pluginInstallUrl} className={linkClass} target="_blank" rel="noreferrer">
                  WordPress.org FAQ
                </a>{" "}
                says most images are ready within 5–10 minutes, depending on queue load and site traffic that triggers
                WP-Cron.
              </li>
              <li>
                <strong>Plan catalog.</strong> Starter is $5/month or $50/year with a 10,000-image quota. Builder is
                $49/month or $99/year with a 100,000-image quota. Growth is $99/month or $199/year with an unlimited
                image quota. Those defaults live in the app catalog and on{" "}
                <Link href="/#pricing" className={linkClass}>
                  pricing
                </Link>
                . A Stripe <code>image_quota</code> feature can replace the quota. The catalog has no per-image price.
              </li>
            </ul>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Decision checklist</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                One unchanging file, and you will maintain the tags? Print the featured image from <code>wp_head</code>.
              </li>
              <li>
                Yoast or Rank Math already owns social tags, and the card is an upload? Set the social image there.
              </li>
              <li>
                The library is posts, pages, or WooCommerce products? Install the OG Pilot plugin and connect OAuth.
              </li>
              <li>
                Both an SEO plugin and OG Pilot would print tags? Set Meta Ownership to External SEO plugin, or let OG
                Pilot own them. Don&apos;t leave both on.
              </li>
              <li>
                The page is outside WordPress, or an agent should create the card? Get an API key or follow the MCP
                guide.
              </li>
            </ol>
          </section>

          <ActivationGuide
            title="Plugin for WordPress OG tags, API key and MCP everywhere else"
            links={[
              {
                href: "/sign_up",
                label: "Get an API key",
                detail: "Sign CDN URLs from PHP, Rails, or any server that is not the WordPress plugin.",
              },
              {
                href: "/docs/mcp",
                label: "Use MCP",
                detail: "Install the remote connector so an agent can generate the card.",
              },
              {
                href: "/new",
                label: "Generate one image",
                detail: "Preview a branded card for a URL that does not live in this WordPress site.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Confirm the permalink before Facebook, X, LinkedIn, or Slack cache it.",
              },
              {
                href: "/docs",
                label: "API docs",
                detail: "JWT claims, the PHP SDK, templates, and the 24-hour iat cache rule.",
              },
              {
                href: "/change-og-image-wordpress",
                label: "Change the OG image",
                detail: "Featured image, SEO-plugin social image, or a generated card, one step at a time.",
              },
              {
                href: "/open-graph-protocol-wordpress",
                label: "Open Graph protocol",
                detail: "What og:title, og:description, og:image, and og:url mean, and who writes them.",
              },
            ]}
          >
            <p>
              Install the{" "}
              <a href={pluginInstallUrl} className={linkClass} target="_blank" rel="noreferrer">
                WordPress plugin
              </a>{" "}
              when the content is posts, pages, or WooCommerce products. It connects over OAuth and can hand tag
              ownership to Yoast, Rank Math, or AIOSEO so you do not get two <code>og:image</code> tags. Get an API key
              when the same templates have to ship from another stack. The{" "}
              <Link href="/nextjs-og-image-generator" className={linkClass}>
                Next.js OG image guide
              </Link>{" "}
              uses that API from <code>generateMetadata</code>. The{" "}
              <Link href="/og-image-api" className={linkClass}>
                OG image API comparison
              </Link>{" "}
              is the same choice against <code>@vercel/og</code> and DIY Satori.
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                WordPress Open Graph plugin questions
              </h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className="mt-20 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-10 text-white shadow-xl">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <p className="text-xs tracking-[0.2em] text-orange-200 uppercase">WordPress + WooCommerce ready</p>
                <h2 className="mt-3 text-3xl font-bold sm:text-4xl">Install the plugin, or sign images from the API</h2>
                <p className="mt-4 max-w-xl text-sm leading-6 text-slate-200">
                  Keep featured images and Yoast or Rank Math fields when the card is a file. Use OG Pilot when the card
                  has to be generated, from the plugin, an API key, or MCP.
                </p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 p-6">
                <div className="flex flex-col gap-3">
                  <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                    <a href={pluginInstallUrl} target="_blank" rel="noreferrer">
                      Install WordPress plugin
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-white/40 bg-transparent text-white hover:bg-white/10"
                  >
                    <Link href="/sign_up">Get API key</Link>
                  </Button>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs/mcp">Use MCP</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/new">Generate</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs">Docs</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/open-graph-debugger">Debugger</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
